'use client';

import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, MapPin, CheckCircle, Heart, Users } from 'lucide-react';

interface QuizQuestion {
  id: string;
  question: string;
  options: Array<{
    value: string;
    label: string;
    destinations: string[];
  }>;
  allowMultiple?: boolean;
}

interface QuizResult {
  destination: string;
  description: string;
  image: string;
  highlights: string[];
  sustainabilityNote?: string;
}

const DestinationQuiz: React.FC = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState<QuizResult | null>(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const questions: QuizQuestion[] = [
    {
      id: 'timing',
      question: 'When do you want to travel?',
      allowMultiple: true,
      options: [
        { value: 'january', label: 'January', destinations: ['Thailand', 'Costa Rica', 'Morocco', 'Peru'] },
        { value: 'february', label: 'February', destinations: ['Thailand', 'Costa Rica', 'Morocco', 'Peru'] },
        { value: 'march', label: 'March', destinations: ['Japan', 'Portugal', 'Morocco', 'Peru'] },
        { value: 'april', label: 'April', destinations: ['Japan', 'Portugal', 'Morocco', 'Peru'] },
        { value: 'may', label: 'May', destinations: ['Japan', 'Portugal', 'Morocco', 'Peru'] },
        { value: 'june', label: 'June', destinations: ['Iceland', 'Norway', 'Costa Rica', 'Nepal'] },
        { value: 'july', label: 'July', destinations: ['Iceland', 'Norway', 'Costa Rica', 'Nepal'] },
        { value: 'august', label: 'August', destinations: ['Iceland', 'Norway', 'Costa Rica', 'Nepal'] },
        { value: 'september', label: 'September', destinations: ['Japan', 'Morocco', 'Peru', 'Nepal'] },
        { value: 'october', label: 'October', destinations: ['Japan', 'Morocco', 'Peru', 'Nepal'] },
        { value: 'november', label: 'November', destinations: ['Japan', 'Morocco', 'Peru', 'Nepal'] },
        { value: 'december', label: 'December', destinations: ['Thailand', 'Costa Rica', 'Morocco', 'Peru'] }
      ]
    },
    {
      id: 'duration',
      question: 'How long do you have for your journey?',
      allowMultiple: true,
      options: [
        { value: 'weekend', label: '1-3 days', destinations: ['Portugal', 'Morocco', 'Japan'] },
        { value: 'short', label: '3-7 days', destinations: ['Morocco', 'Thailand', 'Portugal'] },
        { value: 'medium', label: '1-2 weeks', destinations: ['Japan', 'Costa Rica', 'Nepal', 'Peru'] },
        { value: 'two_to_three_weeks', label: '2-3 weeks', destinations: ['Nepal', 'Peru', 'Japan', 'Iceland'] },
        { value: 'up_to_four_weeks', label: 'up to 4 weeks', destinations: ['Nepal', 'Peru', 'Japan', 'Iceland'] },
        { value: 'extended', label: '1+ months', destinations: ['Nepal', 'Peru', 'Morocco', 'Thailand'] }
      ]
    },
    {
      id: 'experiences',
      question: 'What travel experiences resonate with you?',
      allowMultiple: true,
      options: [
        { value: 'cultural', label: 'Cultural immersion & local traditions', destinations: ['Morocco', 'Japan', 'Peru', 'Nepal'] },
        { value: 'nature', label: 'Nature & wildlife conservation', destinations: ['Costa Rica', 'Iceland', 'Nepal', 'Peru'] },
        { value: 'community', label: 'Community-based tourism', destinations: ['Nepal', 'Morocco', 'Peru', 'Thailand'] },
        { value: 'sustainable', label: 'Sustainable & eco-friendly options', destinations: ['Costa Rica', 'Iceland', 'Nepal', 'Morocco'] },
        { value: 'adventure', label: 'Adventure & outdoor activities', destinations: ['Nepal', 'Peru', 'Iceland', 'Costa Rica'] },
        { value: 'wellness', label: 'Wellness & mindfulness', destinations: ['Thailand', 'Morocco', 'Japan', 'Costa Rica'] },
        { value: 'culinary', label: 'Culinary experiences with locals', destinations: ['Thailand', 'Morocco', 'Japan', 'Peru'] },
        { value: 'learning', label: 'Learning new skills or crafts', destinations: ['Morocco', 'Japan', 'Thailand', 'Peru'] }
      ]
    },
    {
      id: 'climate',
      question: 'What climate and environment appeal to you?',
      allowMultiple: true,
      options: [
        { value: 'tropical', label: 'Tropical & warm', destinations: ['Thailand', 'Costa Rica'] },
        { value: 'temperate', label: 'Temperate & mild', destinations: ['Japan', 'Portugal', 'Morocco'] },
        { value: 'cool', label: 'Cool & crisp', destinations: ['Iceland', 'Nepal', 'Peru'] },
        { value: 'desert', label: 'Desert & dry', destinations: ['Morocco', 'Peru'] },
        { value: 'continental', label: 'Continental (seasonal variation)', destinations: ['Japan', 'Nepal', 'Portugal'] },
        { value: 'polar', label: 'Polar & snowy', destinations: ['Iceland'] },
        { value: 'mountain', label: 'Mountain landscapes', destinations: ['Nepal', 'Peru', 'Iceland', 'Morocco'] },
        { value: 'coastal', label: 'Coastal areas', destinations: ['Thailand', 'Costa Rica', 'Portugal', 'Morocco'] }
      ]
    },
    {
      id: 'group',
      question: 'Who are you traveling with?',
      allowMultiple: true,
      options: [
        { value: 'solo', label: 'Solo traveler', destinations: ['Japan', 'Iceland', 'Thailand', 'Morocco'] },
        { value: 'couple', label: 'Couple/romantic partner', destinations: ['Japan', 'Morocco', 'Thailand', 'Iceland'] },
        { value: 'family', label: 'Family with children', destinations: ['Thailand', 'Costa Rica', 'Portugal', 'Morocco'] },
        { value: 'friends', label: 'Group of friends', destinations: ['Thailand', 'Nepal', 'Peru', 'Iceland'] },
        { value: 'multigen', label: 'Multi-generational family', destinations: ['Portugal', 'Morocco', 'Thailand', 'Japan'] },
        { value: 'corporate', label: 'Team/company offsite', destinations: ['Portugal', 'Japan', 'Costa Rica', 'Morocco'] }
      ]
    },
    {
      id: 'impact',
      question: 'What impact do you want your travel to have?',
      allowMultiple: true,
      options: [
        { value: 'support_local', label: 'Support local communities economically', destinations: ['Nepal', 'Morocco', 'Peru', 'Thailand'] },
        { value: 'learn_culture', label: 'Learn about different cultures', destinations: ['Japan', 'Morocco', 'Peru', 'Nepal'] },
        { value: 'conservation', label: 'Contribute to conservation efforts', destinations: ['Costa Rica', 'Iceland', 'Nepal', 'Peru'] },
        { value: 'memories', label: 'Create lasting memories with loved ones', destinations: ['Japan', 'Thailand', 'Morocco', 'Iceland'] },
        { value: 'challenge', label: 'Challenge myself with new experiences', destinations: ['Nepal', 'Peru', 'Iceland', 'Morocco'] },
        { value: 'relax', label: 'Relax and recharge', destinations: ['Thailand', 'Costa Rica', 'Portugal', 'Morocco'] },
        { value: 'team_building', label: 'Strengthen team spirit & collaboration', destinations: ['Portugal', 'Japan', 'Costa Rica', 'Morocco'] },
        { value: 'low_impact', label: 'Minimize environmental footprint', destinations: ['Costa Rica', 'Japan', 'Portugal', 'Iceland'] }
      ]
    }
  ];

  const destinations: Record<string, QuizResult> = {
    'Costa Rica': {
      destination: 'Costa Rica',
      description: 'Experience incredible biodiversity, pristine beaches, and adventure activities in this Central American paradise.',
      image: 'https://images.pexels.com/photos/1268855/pexels-photo-1268855.jpeg',
      highlights: [
        'Incredible wildlife and national parks',
        'Beautiful Pacific and Caribbean coasts',
        'Adventure activities like zip-lining and hiking'
      ],
      sustainabilityNote: 'Pioneer in eco-tourism with 25% of land protected'
    },
    'Thailand': {
      destination: 'Thailand',
      description: 'Discover ancient temples, vibrant cities, stunning islands, and world-renowned cuisine in Southeast Asia.',
      image: 'https://images.pexels.com/photos/2412603/pexels-photo-2412603.jpeg',
      highlights: [
        'Rich cultural heritage and temples',
        'Stunning beaches and tropical islands',
        'Amazing street food and cuisine'
      ],
      sustainabilityNote: 'Strong community-based tourism initiatives'
    },
    'Japan': {
      destination: 'Japan',
      description: 'Immerse yourself in a unique blend of ancient traditions and modern innovation in the Land of the Rising Sun.',
      image: 'https://images.pexels.com/photos/161401/fuji-mountain-mountain-peak-volcano-161401.jpeg',
      highlights: [
        'Ancient temples and traditional culture',
        'Modern cities and cutting-edge technology',
        'Beautiful cherry blossoms and gardens'
      ],
      sustainabilityNote: 'Excellent public transport and sustainable practices'
    },
    'Iceland': {
      destination: 'Iceland',
      description: 'Witness otherworldly landscapes, the Northern Lights, and incredible natural phenomena in this Nordic wonderland.',
      image: 'https://images.pexels.com/photos/417074/pexels-photo-417074.jpeg',
      highlights: [
        'Spectacular Northern Lights',
        'Unique volcanic landscapes and geysers',
        'Incredible waterfalls and glaciers'
      ],
      sustainabilityNote: '100% renewable energy and strict environmental protection'
    },
    'Morocco': {
      destination: 'Morocco',
      description: 'Explore vibrant souks, ancient medinas, desert adventures, and rich Berber culture in North Africa.',
      image: 'https://images.pexels.com/photos/2440009/pexels-photo-2440009.jpeg',
      highlights: [
        'Historic cities and architectural marvels',
        'Sahara Desert adventures',
        'Vibrant souks and traditional crafts'
      ],
      sustainabilityNote: 'Growing sustainable tourism and local community support'
    },
    'Nepal': {
      destination: 'Nepal',
      description: 'Experience the majestic Himalayas, ancient temples, and warm hospitality in this mountain kingdom.',
      image: 'https://images.pexels.com/photos/2437291/pexels-photo-2437291.jpeg',
      highlights: [
        'Spectacular Himalayan mountain ranges',
        'Rich Buddhist and Hindu heritage',
        'World-class trekking and adventure'
      ],
      sustainabilityNote: 'Strong community-based tourism and conservation efforts'
    },
    'Peru': {
      destination: 'Peru',
      description: 'Discover ancient Inca civilization, diverse landscapes, and vibrant Andean culture in South America.',
      image: 'https://images.pexels.com/photos/2376997/pexels-photo-2376997.jpeg',
      highlights: [
        'Machu Picchu and ancient Inca sites',
        'Diverse ecosystems from Amazon to Andes',
        'Rich indigenous culture and traditions'
      ],
      sustainabilityNote: 'Community tourism initiatives preserving local heritage'
    },
    'Portugal': {
      destination: 'Portugal',
      description: 'Explore charming coastal towns, historic cities, and warm hospitality in this European gem.',
      image: 'https://images.pexels.com/photos/1570618/pexels-photo-1570618.jpeg',
      highlights: [
        'Beautiful Atlantic coastline',
        'Rich maritime history and culture',
        'Delicious cuisine and world-class wines'
      ],
      sustainabilityNote: 'Growing sustainable tourism and local community focus'
    }
  };

  const handleAnswer = (questionId: string, value: string) => {
    setAnswers(prev => {
      const currentAnswers = prev[questionId] || [];
      const question = questions.find(q => q.id === questionId);
      
      if (question?.allowMultiple) {
        // Toggle selection for multiple choice questions
        if (currentAnswers.includes(value)) {
          return { ...prev, [questionId]: currentAnswers.filter(v => v !== value) };
        } else {
          return { ...prev, [questionId]: [...currentAnswers, value] };
        }
      } else {
        // Single selection for non-multiple choice questions
        return { ...prev, [questionId]: [value] };
      }
    });
  };

  const calculateResult = () => {
    // Count destination occurrences across all answers
    const destinationCounts: Record<string, number> = {};
    
    questions.forEach(question => {
      const questionAnswers = answers[question.id] || [];
      questionAnswers.forEach(answerValue => {
        const selectedOption = question.options.find(opt => opt.value === answerValue);
        if (selectedOption) {
          selectedOption.destinations.forEach(dest => {
            destinationCounts[dest] = (destinationCounts[dest] || 0) + 1;
          });
        }
      });
    });

    // Find the destination with the highest count
    const topDestination = Object.entries(destinationCounts)
      .sort(([,a], [,b]) => b - a)[0]?.[0];

    // Fallback to a default destination if no clear winner
    const finalDestination = topDestination || 'Costa Rica';
    
    setResult(destinations[finalDestination] || destinations['Costa Rica']);
    setShowResult(true);
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateResult();
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
    setResult(null);
    setShowLeadForm(false);
    setEmail('');
    setName('');
    setMessage('');
    setIsSubmitted(false);
  };

  const getOptionLabel = (questionId: string, value: string): string => {
    const question = questions.find(q => q.id === questionId);
    const option = question?.options.find(o => o.value === value);
    return option?.label || value;
  };

  const isQuestionAnswered = (questionId: string) => {
    const questionAnswers = answers[questionId] || [];
    return questionAnswers.length > 0;
  };

  const generateConciseSummary = (): string => {
    const get = (id: string) => (answers[id] || []).map(v => getOptionLabel(id, v));
    const month = get('timing')[0];
    const duration = get('duration')[0];
    const experiences = get('experiences').slice(0, 3);
    const climate = get('climate')[0];
    const group = get('group')[0];
    const impact = get('impact').slice(0, 3);

    const lines: string[] = [];
    if (month) lines.push(`Travel month: ${month}`);
    if (duration) lines.push(`Duration: ${duration}`);
    if (group) lines.push(`Travel party: ${group}`);
    if (experiences.length) lines.push(`Top interests: ${experiences.join(', ')}`);
    if (climate) lines.push(`Preferred climate: ${climate}`);
    if (impact.length) lines.push(`Goals: ${impact.join(', ')}`);
    lines.push('Special notes:');
    return lines.join('\n');
  };

  const handleGetTripProposal = () => {
    if (!message) {
      setMessage(generateConciseSummary());
    }
    setShowLeadForm(true);
  };

  const handleSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/quiz-submission', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          message,
          quizAnswers: answers,
          recommendedDestination: result?.destination,
          source: 'destination-quiz'
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit quiz');
      }
      
      const data = await response.json();
      console.log('Quiz submission successful:', data);
      
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      // You could add error state handling here if needed
      alert('There was an error submitting your quiz. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showLeadForm && result) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-4 max-w-xl mx-auto">
        <div className="text-center">
          <MapPin className="h-12 w-12 text-primary-600 mx-auto mb-3" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Perfect! We&apos;ll create your personalized itinerary
          </h3>
          <p className="text-gray-600 mb-6 text-sm leading-relaxed">
            Based on your quiz answers, we&apos;ll create a detailed trip proposal for <strong>{result.destination}</strong> tailored to your preferences.
          </p>
          
          {!isSubmitted ? (
            <form onSubmit={handleSubmitEmail} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="text-left">
                  <label htmlFor="name" className="sr-only">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 text-sm"
                    placeholder="Full name"
                  />
                </div>
                <div className="text-left">
                  <label htmlFor="email" className="sr-only">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 text-sm"
                    placeholder="Email address"
                  />
                </div>
              </div>

              {/* Review summary moved into the editable message field below for simpler editing */}

              <div className="text-left">
                <label htmlFor="message" className="sr-only">Edit your answers or add details</label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 resize-none text-sm"
                  placeholder="Edit your answers summary or add anything else that helps us personalize your itinerary"
                />
                <p className="text-xs text-gray-500 mt-1">You can edit text above instead of going back through the quiz.</p>
              </div>

              <div className="space-y-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary-600 text-white px-4 py-2 rounded-md font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-sm"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      Get My Trip Proposal
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowLeadForm(false)}
                  className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md font-medium hover:bg-gray-200 transition-colors text-sm"
                >
                  Back to Results
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center justify-center space-x-2 mb-3">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                  <span className="text-xl font-semibold text-green-800">Success!</span>
                </div>
                <p className="text-green-700">
                  We&apos;ve received your request, {name}! We&apos;ll send your personalized trip proposal for <strong>{result.destination}</strong> to <strong>{email}</strong> within 24 hours.
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  <span className="font-semibold text-blue-800">What happens next?</span>
                </div>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>• Our travel experts review your preferences</li>
                  <li>• We create a personalized itinerary</li>
                  <li>• You receive your trip proposal via email</li>
                  <li>• We connect you with local DMCs in {result.destination}</li>
                </ul>
              </div>
              
              <button
                onClick={restartQuiz}
                className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors"
              >
                Take Quiz Again
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (showResult && result) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
        <div className="text-center">
          <MapPin className="h-16 w-16 text-primary-600 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Your Perfect Destination Match:
          </h3>
          <h4 className="text-3xl font-serif text-primary-600 mb-4">
            {result.destination}
          </h4>
          <p className="text-gray-600 mb-6 leading-relaxed">
            {result.description}
          </p>
          
          <div className="space-y-3 mb-6">
            {result.highlights.map((highlight, index) => (
              <div key={index} className="flex items-center justify-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="text-gray-700">{highlight}</span>
              </div>
            ))}
          </div>

          {result.sustainabilityNote && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Heart className="h-5 w-5 text-green-600" />
                <span className="font-semibold text-green-800">Sustainable Travel</span>
              </div>
              <p className="text-green-700 text-sm">{result.sustainabilityNote}</p>
            </div>
          )}

          <div className="border border-gray-200 rounded-lg p-4 mb-8">
            <div className="flex items-center justify-center space-x-2 mb-1">
              <Users className="h-5 w-5 text-gray-600" />
              <span className="font-semibold text-gray-800">How do you like this country match?</span>
            </div>
            <p className="text-gray-700 text-sm mb-3 text-center">
              We&apos;d love to send you a personalized itinerary for <strong>{result.destination}</strong> based on your answers.
            </p>
            <button
              onClick={handleGetTripProposal}
              className="w-full bg-primary-600 text-white px-4 py-2 rounded-md font-medium hover:bg-primary-700 transition-colors text-sm"
            >
              Get My Trip Proposal
            </button>
          </div>

          <div className="space-y-3">
            <button
              onClick={restartQuiz}
              className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md font-medium hover:bg-gray-200 transition-colors text-sm"
            >
              Take Quiz Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const isAnswered = isQuestionAnswered(currentQ.id);
  const currentAnswers = answers[currentQ.id] || [];

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center space-x-2 mb-4">
          {questions.map((_, index) => (
            <div
              key={index}
              className={`h-2 w-8 rounded-full ${
                index <= currentQuestion ? 'bg-primary-600' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Question {currentQuestion + 1} of {questions.length}
        </h3>
        <h4 className="text-2xl font-serif text-primary-600">
          {currentQ.question}
        </h4>
        {currentQ.allowMultiple && (
          <p className="text-sm text-gray-500 mt-2">
            Select all that apply
          </p>
        )}
      </div>

      <div className="mb-8">
        {currentQ.id === 'timing' ? (
          <div className="grid grid-cols-3 gap-3 w-full" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
            {currentQ.options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswer(currentQ.id, option.value)}
                className={`p-3 text-center rounded-lg border-2 transition-all duration-200 w-full ${
                  currentAnswers.includes(option.value)
                    ? 'border-primary-600 bg-primary-50 text-primary-900'
                    : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex flex-col items-center justify-center space-y-1">
                  <span className="font-medium text-sm">{option.label}</span>
                  {currentAnswers.includes(option.value) && (
                    <CheckCircle className="h-4 w-4 text-primary-600" />
                  )}
                </div>
              </button>
            ))}
          </div>
        ) : currentQ.id === 'experiences' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {currentQ.options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswer(currentQ.id, option.value)}
                className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 ${
                  currentAnswers.includes(option.value)
                    ? 'border-primary-600 bg-primary-50 text-primary-900'
                    : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{option.label}</span>
                  {currentAnswers.includes(option.value) && (
                    <CheckCircle className="h-4 w-4 text-primary-600" />
                  )}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {currentQ.options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswer(currentQ.id, option.value)}
                className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 ${
                  currentAnswers.includes(option.value)
                    ? 'border-primary-600 bg-primary-50 text-primary-900'
                    : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{option.label}</span>
                  {currentAnswers.includes(option.value) && (
                    <CheckCircle className="h-4 w-4 text-primary-600" />
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button
          onClick={prevQuestion}
          disabled={currentQuestion === 0}
          className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Previous
        </button>
        
        <button
          onClick={nextQuestion}
          disabled={!isAnswered}
          className="flex items-center px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {currentQuestion === questions.length - 1 ? 'Get My Result' : 'Next'}
          <ArrowRight className="ml-2 h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default DestinationQuiz;
